import { Component, ElementRef, ViewChild, OnInit, AfterViewChecked } from '@angular/core';
import { SharedService } from '../../shared/services/shared.service';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../environments/environment';
import { MatCard, MatCardHeader, MatCardTitle, MatCardContent } from '@angular/material/card';
import { VideoCardComponent } from '../video-card/video-card.component';
import { FormsModule } from '@angular/forms';
import { NgClass, DatePipe } from '@angular/common';

@Component({
    selector: 'app-video-library',
    templateUrl: './video-library.component.html',
    styleUrl: './video-library.component.css',
    imports: [RouterLink, MatCard, VideoCardComponent, MatCardHeader, MatCardTitle, MatCardContent, FormsModule, NgClass, DatePipe]
})
export class VideoLibraryComponent implements AfterViewChecked, OnInit {
  @ViewChild('videoIframe') videoIframe!: ElementRef;
  public singleVideo:string = 'singleVideo';

  searchQuery = '';
  sortOption = 'date';
  sortDirection: 'desc' | 'asc' = 'desc';
  selectedCategory = '';
  public categories: any = [];
  public videos: any = [];
  selectedVideoId: string | null = null;
  selectedVideo: any = null;
  isVideoLibraryRoute = false;
  hasAutoplayTriggered = false

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private sharedService: SharedService,
    public toaster: ToastrService,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit() {
    this.isVideoLibraryRoute = this.router.url === '/video-library';
    this.activatedRoute.queryParams.subscribe(params => {
      if(params['category']) {
        const category = params['category'];
        this.selectedCategory = category;
        this.searchQuery = category;
      }
    });
    this.route.paramMap.subscribe(params => {
      this.selectedVideoId = params.get('id');
      this.getVideoLibrary();
    });
  }

  get filteredVideos() {
    const query = this.searchQuery.toLowerCase();

    if (!query) {
      this.selectedCategory = '';
    }
    return this.videos.filter(
      (video: any) => video.title.toLowerCase().includes(query) || video.category.toLowerCase().includes(query)
    );
  }

  trackByVideo = (_: number, video: any): any => {
    return video?.id ?? video?.video_blobname ?? video?.videoBlobName ?? video?.title;
  };

  selectCategory(category: string) {
    if (!category || this.selectedCategory === category) {
      // Deselect if already selected
      this.selectedCategory = '';
      this.searchQuery = '';
    } else {
      // Select new category
      this.selectedCategory = category;
      this.searchQuery = category;
    }
  }

  getVideoLibrary() {
    if (this.sharedService && this.sharedService.getVideoLibrary) {
      this.sharedService.getVideoLibrary().subscribe({
        next: response => {
          this.videos = response.result
            // .map(video => ({
            //   ...video,
            //   is_playing: video.is_playing === 'True'
            // }))
              .map((video: any) => this.normalizeVideoItem(video))
            .sort((a: any, b: any) => a.occurence - b.occurence);
          this.categories = [...new Set(this.videos.map((v: any) => v.category))];
          if (this.selectedVideoId) {
            this.selectedVideo = this.videos.find((v: any) => v.id == this.selectedVideoId);
          }
        },
        error: () => {
          this.toaster.error('Failed to fetch video library');
        }
      });
    } else {
      console.error('SharedService or getVideoLibrary method is not defined.');
    }
  }

  goToLibrary() {
    this.router.navigate(['/video-library']);
  }

  ngAfterViewChecked() {
    if (this.selectedVideo && this.videoIframe) {
      const iframe = this.videoIframe.nativeElement;
      console.log('iframe: ', iframe); // Only run once

      if (!this.hasAutoplayTriggered) {
        this.hasAutoplayTriggered = true;

        const clickEvent = new MouseEvent('click', { bubbles: true, cancelable: true });
        iframe.dispatchEvent(clickEvent);
      }
    }
  }

  private stripTokenQueryParam(url: string): string {
    if (!url) return '';
    try {
      const parsed = new URL(url, environment.apiUrl);
      parsed.searchParams.delete('token');
      parsed.searchParams.delete('access_token');
      return parsed.toString();
    } catch {
      return url;
    }
  }

  private rewriteSyncBlobVideoPathToQuery(url: string): string {
    if (!url) return '';

    try {
      const parsed = new URL(url, environment.apiUrl);
      const apiBase = new URL(environment.apiUrl);
      // Origin check removed to allow public video/thumb endpoints without authentication
      // if (parsed.origin !== apiBase.origin) return url;

      const marker = '/v4/sync/blob/video/';
      const queryEndpointPath = '/v4/sync/blob/video';

      const safeDecode = (value: string): string => {
        try {
          return decodeURIComponent(value);
        } catch {
          return value;
        }
      };

      const encodeForQueryKeepSlashes = (decoded: string): string => {
        return encodeURIComponent(decoded).replace(/%2F/gi, '/');
      };

      // If it's already query-style, normalize blobName encoding for readability.
      if (parsed.pathname === queryEndpointPath && parsed.searchParams.has('blobName')) {
        const decodedBlobName = parsed.searchParams.get('blobName') ?? '';
        const encodedForQuery = encodeForQueryKeepSlashes(decodedBlobName);

        const pairs: string[] = [];
        parsed.searchParams.forEach((value, key) => {
          if (key === 'blobName') return;
          pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        });
        pairs.push(`blobName=${encodedForQuery}`);

        const query = pairs.length ? `?${pairs.join('&')}` : '';
        return `${parsed.origin}${queryEndpointPath}${query}${parsed.hash || ''}`;
      }

      if (!parsed.pathname.startsWith(marker)) return url;

      const nameFromPath = parsed.pathname.substring(marker.length);
      const decodedBlobName = safeDecode(nameFromPath);
      const encodedForQuery = encodeForQueryKeepSlashes(decodedBlobName);

      const endpointPath = marker.slice(0, -1);

      const pairs: string[] = [];
      parsed.searchParams.forEach((value, key) => {
        if (key === 'blobName') return;
        pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      });
      pairs.push(`blobName=${encodedForQuery}`);

      const query = pairs.length ? `?${pairs.join('&')}` : '';
      return `${parsed.origin}${endpointPath}${query}${parsed.hash || ''}`;
    } catch {
      return url;
    }
  }

  private buildMediaUrl(url?: string | null): string {
    if (!url) {
      return '';
    }

    const trimmedUrl = url.trim();
    if (trimmedUrl.length === 0) {
      return '';
    }

    // Some APIs return string placeholders instead of null.
    const lower = trimmedUrl.toLowerCase();
    if (lower === 'null' || lower === 'undefined') {
      return '';
    }

    const isAbsoluteUrl = /^https?:\/\//i.test(trimmedUrl);
    const normalizedUrl = isAbsoluteUrl
      ? trimmedUrl
      : `${environment.apiUrl}${trimmedUrl.startsWith('/') ? trimmedUrl : `/${trimmedUrl}`}`;

    // IMPORTANT: never attach tokens in query params.
    return this.rewriteSyncBlobVideoPathToQuery(this.stripTokenQueryParam(normalizedUrl));
  }

  private normalizeVideoItem(video: any): any {
    const flattenedVideo = video?.dataValues ? { ...video.dataValues, ...video } : video;

    const thumbnailPost = flattenedVideo?.thumbnailPost;

    const videoPost = flattenedVideo?.videoPost;

    const rawVideoUrl = flattenedVideo?.videoUrl;
    const videoUrl = this.buildMediaUrl(rawVideoUrl);
    const vttUrl = this.buildMediaUrl(flattenedVideo?.vttUrl);

    return {
      ...flattenedVideo,
      // videoUrl: videoUrl,
      videoUrl,
      vttUrl,
      thumbnailPost,
      videoPost,
      is_playing: `${flattenedVideo?.is_playing}`.toLowerCase() === 'true'
    };
  }
}
